/**
 * ConnectionFormPanel — Integration Hub
 * -----------------------------------------------------------------------------
 * Formulário de criação/edição de conexão renderizado COMO VISTA (não como
 * modal), tanto dentro do modal "Aplicativos e integrações" quanto na página
 * fixa do conector. Isso elimina o antigo "modal dentro de modal" e dá espaço
 * para providers com muitos recursos.
 *
 * Layout em duas colunas:
 *   - Esquerda: identificação, URL base e credenciais (dinâmicas por auth_type)
 *   - Direita: recursos deste aplicativo (busca, agrupamento, contador)
 *
 * Segurança:
 *   - O segredo é enviado apenas no salvamento e nunca é lido de volta.
 *   - Ao editar, o campo fica vazio e só é regravado se preenchido.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-24 v1.1.0 - Suporte a aplicativos de SAÍDA com transporte de
 *          banco de dados (transport = 'mysql'): campos host/porta/banco no lugar
 *          da URL base e coluna de recursos substituída por aviso de envio.
 * RELEASE: 2026-08-23 v1.0.0 - Extraído de ConnectionFormDialog (fim do modal
 *          aninhado) com layout de duas colunas e ajuda por recurso.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Puzzle, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProviderOperationList } from '@/components/data/ProviderOperationList';
import { MarkdownBlock } from '@/components/data/ProviderDocsPanel';
import type {
  IntegrationConnection,
  IntegrationProvider,
  SaveConnectionInput,
} from '@/hooks/integrations/useIntegrationConnections';

/** Estado dos campos de credencial (união de todos os tipos suportados) */
interface CredentialState {
  key_name: string;
  key_value: string;
  key_location: 'header' | 'query';
  token: string;
  username: string;
  password: string;
}

const EMPTY_CREDENTIALS: CredentialState = {
  key_name: 'x-api-key',
  key_value: '',
  key_location: 'header',
  token: '',
  username: '',
  password: '',
};

interface ConnectionFormPanelProps {
  /** Provider fixado (a vista sempre nasce de dentro do conector) */
  provider: IntegrationProvider;
  /** Conexão em edição (null = criação) */
  connection?: IntegrationConnection | null;
  isSaving: boolean;
  onSubmit: (input: SaveConnectionInput) => Promise<unknown>;
  /** Volta para a vista de detalhes do conector */
  onCancel: () => void;
  /** Disparado após salvar com sucesso */
  onSaved?: () => void;
}

export function ConnectionFormPanel({
  provider,
  connection,
  isSaving,
  onSubmit,
  onCancel,
  onSaved,
}: ConnectionFormPanelProps) {
  const isEditing = Boolean(connection?.id);

  /** Aplicativo de saída por banco de dados (não usa URL/recursos de leitura) */
  const isDatabase = provider.transport === 'mysql';

  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [credentials, setCredentials] = useState<CredentialState>(EMPTY_CREDENTIALS);
  /** Recursos (operações) marcados pelo usuário nesta conexão */
  const [enabledOps, setEnabledOps] = useState<string[]>([]);
  /** Dados de conexão do banco de destino (guardados em config, sem segredo) */
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState('3306');
  const [dbName, setDbName] = useState('');

  // Preenche o formulário sempre que a conexão em edição muda
  useEffect(() => {
    setName(connection?.name ?? '');
    setBaseUrl(connection?.base_url ?? '');
    setIsActive(connection?.is_active ?? true);
    setCredentials(EMPTY_CREDENTIALS);

    // Configuração do banco (apenas dados não sensíveis ficam em config)
    const config = (connection?.config ?? {}) as Record<string, unknown>;
    setDbHost(String(config.host ?? ''));
    setDbPort(String(config.port ?? 3306));
    setDbName(String(config.database ?? ''));

    if (connection?.id) {
      // Edição: reflete o que já está habilitado na conexão
      setEnabledOps(
        (connection.enabled_operations ?? []).filter((op) => op.is_enabled).map((op) => op.code),
      );
    } else {
      // Criação: todos os recursos vêm marcados por padrão
      setEnabledOps((provider.operations ?? []).map((op) => op.code));
    }
  }, [connection, provider]);

  /** Marca/desmarca um recurso */
  const toggleOperation = (code: string, checked: boolean) => {
    setEnabledOps((prev) =>
      checked ? Array.from(new Set([...prev, code])) : prev.filter((c) => c !== code),
    );
  };

  /** Atualiza um campo de credencial */
  const setCredential = (field: keyof CredentialState, value: string) => {
    setCredentials((prev) => ({ ...prev, [field]: value } as CredentialState));
  };

  /** Monta o objeto de credencial conforme o auth_type do provider */
  const buildCredentials = (): Record<string, unknown> | null => {
    switch (provider.auth_type) {
      case 'api_key':
        return credentials.key_value
          ? {
              key_name: credentials.key_name.trim() || 'x-api-key',
              key_value: credentials.key_value.trim(),
              key_location: credentials.key_location,
            }
          : null;
      case 'bearer_token':
        return credentials.token ? { token: credentials.token.trim() } : null;
      case 'basic_auth':
        return credentials.username
          ? { username: credentials.username.trim(), password: credentials.password }
          : null;
      default:
        return null;
    }
  };

  /** Porta válida (1..65535) — validação também existe no servidor/transporte */
  const portNumber = Number(dbPort);
  const isPortValid =
    Number.isInteger(portNumber) && portNumber >= 1 && portNumber <= 65535;

  const canSubmit =
    Boolean(name.trim()) &&
    !isSaving &&
    (!isDatabase || (Boolean(dbHost.trim()) && Boolean(dbName.trim()) && isPortValid));

  /** Valida e envia o formulário */
  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    // Aplicativos de banco guardam host/porta/base em config (nunca segredo)
    const config = isDatabase
      ? {
          host: dbHost.trim().slice(0, 255),
          port: portNumber,
          database: dbName.trim().slice(0, 120),
        }
      : undefined;

    await onSubmit({
      connectionId: connection?.id ?? null,
      name: trimmedName.slice(0, 120),
      providerSlug: provider.slug,
      baseUrl: isDatabase || !baseUrl.trim() ? null : baseUrl.trim().slice(0, 500),
      isActive,
      config,
      credentials: buildCredentials(),
      credentialId: connection?.credential_id ?? null,
      // Recursos escolhidos pelo usuário (lista pode ser vazia)
      enabledOperationCodes: enabledOps,
    });

    onSaved?.();
  };

  const setupGuide = useMemo(() => provider.setup_guide_md ?? null, [provider]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ───────────── Breadcrumb ───────────── */}
      <div className="shrink-0 px-4 pt-4 sm:px-6">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {provider.name} / {isEditing ? 'Editar conexão' : 'Nova conexão'}
        </button>
        <h2 className="mt-2 text-lg font-semibold">
          {isEditing ? 'Editar conexão' : 'Nova conexão'}
        </h2>
        <p className="text-xs text-muted-foreground">
          As credenciais ficam guardadas no servidor e nunca são exibidas novamente.
        </p>
      </div>

      {/* ───────────── Conteúdo em duas colunas ───────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <div className="grid gap-5 lg:grid-cols-2">
          {/* Coluna esquerda: identificação + credenciais */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="connection-name">Nome da conexão</Label>
              <Input
                id="connection-name"
                value={name}
                maxLength={120}
                placeholder={`Ex.: ${provider.name} - Produção`}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {isDatabase ? (
              /* Aplicativo de saída por banco: endereço do servidor de destino */
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="db-host">Servidor (host)</Label>
                  <Input
                    id="db-host"
                    value={dbHost}
                    maxLength={255}
                    placeholder="mysql.exemplo.com.br"
                    onChange={(e) => setDbHost(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="db-port">Porta</Label>
                  <Input
                    id="db-port"
                    inputMode="numeric"
                    value={dbPort}
                    maxLength={5}
                    placeholder="3306"
                    onChange={(e) => setDbPort(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-3">
                  <Label htmlFor="db-name">Nome do banco de dados</Label>
                  <Input
                    id="db-name"
                    value={dbName}
                    maxLength={120}
                    placeholder="base_do_cliente"
                    onChange={(e) => setDbName(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="connection-url">URL base da API</Label>
                <Input
                  id="connection-url"
                  value={baseUrl}
                  maxLength={500}
                  placeholder="https://api.exemplo.com.br"
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
              </div>
            )}

            {/* Credenciais dinâmicas por tipo de autenticação */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Credenciais de acesso
              </div>

              {isEditing && (
                <InlineNotice variant="info">
                  Deixe em branco para manter a credencial atual.
                </InlineNotice>
              )}

              {provider.auth_type === 'api_key' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {/*
                    Providers gerenciados (integração nativa): o nome do parâmetro e
                    o local de envio da chave são definidos pelo catálogo e aplicados
                    no servidor — expor esses campos só geraria erro de configuração.
                  */}
                  {!provider.is_managed && (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="cred-key-name">Nome do parâmetro</Label>
                        <Input
                          id="cred-key-name"
                          value={credentials.key_name}
                          maxLength={80}
                          onChange={(e) => setCredential('key_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Enviar em</Label>
                        <Select
                          value={credentials.key_location}
                          onValueChange={(v) => setCredential('key_location', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="header">Cabeçalho (header)</SelectItem>
                            <SelectItem value="query">Parâmetro na URL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="cred-key-value">Chave de API</Label>
                    <Input
                      id="cred-key-value"
                      type="password"
                      autoComplete="new-password"
                      value={credentials.key_value}
                      maxLength={500}
                      placeholder={isEditing ? '••••••••' : 'Cole a chave fornecida pelo sistema'}
                      onChange={(e) => setCredential('key_value', e.target.value)}
                    />
                  </div>
                  {provider.is_managed && (
                    <div className="sm:col-span-2">
                      <InlineNotice variant="info">
                        A configuração técnica desta integração é automática. Informe apenas a
                        URL da API fornecida para a sua empresa e a chave de acesso.
                      </InlineNotice>
                    </div>
                  )}
                </div>
              )}

              {provider.auth_type === 'bearer_token' && (
                <div className="space-y-1.5">
                  <Label htmlFor="cred-token">Token de acesso</Label>
                  <Input
                    id="cred-token"
                    type="password"
                    autoComplete="new-password"
                    value={credentials.token}
                    maxLength={2000}
                    placeholder={isEditing ? '••••••••' : 'Cole o token fornecido pelo sistema'}
                    onChange={(e) => setCredential('token', e.target.value)}
                  />
                </div>
              )}

              {provider.auth_type === 'basic_auth' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="cred-user">Usuário</Label>
                    <Input
                      id="cred-user"
                      value={credentials.username}
                      maxLength={200}
                      onChange={(e) => setCredential('username', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cred-pass">Senha</Label>
                    <Input
                      id="cred-pass"
                      type="password"
                      autoComplete="new-password"
                      value={credentials.password}
                      maxLength={500}
                      onChange={(e) => setCredential('password', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {provider.auth_type === 'custom' && (
                <InlineNotice variant="warning">
                  Este sistema usa autenticação personalizada. Informe a URL base e salve; os campos
                  específicos são configurados junto com a operação.
                </InlineNotice>
              )}
            </div>

            {/* Situação */}
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Conexão ativa</p>
                <p className="text-xs text-muted-foreground">
                  Conexões inativas não executam operações nem importações.
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>

            {/* Passo a passo do provider (documentação vinda do banco) */}
            {setupGuide && (
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="mb-1 text-sm font-medium">Como obter as credenciais</p>
                <MarkdownBlock content={setupGuide} />
              </div>
            )}
          </div>

          {/* Coluna direita: recursos (leitura) ou explicação do envio (banco) */}
          {isDatabase ? (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Puzzle className="h-4 w-4 text-primary" />
                Envio de dados
              </div>

              <p className="text-xs text-muted-foreground">
                Este aplicativo é de <strong>saída</strong>: ele não consulta dados, apenas envia.
                Depois de salvar e testar a conexão, ative o envio automático na aba{' '}
                <strong>Envios</strong> para que os contratos assinados sejam gravados no sistema de
                destino.
              </p>

              <InlineNotice variant="info">
                As tabelas e colunas de destino são definidas pelo catálogo do aplicativo e não podem
                ser alteradas pela interface, garantindo que nenhum outro dado do banco seja tocado.
              </InlineNotice>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Puzzle className="h-4 w-4 text-primary" />
                Recursos deste aplicativo
              </div>

              <p className="text-xs text-muted-foreground">
                Marque o que deseja usar dentro do sistema. Recursos desmarcados ficam indisponíveis
                nas telas e automações que dependem deste aplicativo.
              </p>

              <ProviderOperationList
                operations={provider.operations}
                enabledCodes={enabledOps}
                onToggle={toggleOperation}
                mode="select"
                disabled={isSaving}
              />

              {provider.operations.length > 0 && enabledOps.length === 0 && (
                <InlineNotice variant="warning">
                  Nenhum recurso marcado: a conexão será salva, mas não será oferecida em nenhuma
                  tela do sistema.
                </InlineNotice>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ───────────── Rodapé fixo ───────────── */}
      <div className="flex shrink-0 justify-end gap-2 border-t bg-muted/30 px-4 py-3 sm:px-6">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSaving ? 'Salvando...' : 'Salvar conexão'}
        </Button>
      </div>
    </div>
  );
}
